import { AlertCircle, Check, DollarSign, Tag } from "lucide-react";
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import ButtonLoadingSpinner from "../../../shared/components/ButtonLoadingCommponent";
import { SectionTitle } from "./SectionTitle";

interface PriceSectionProps {
  totalPrice: number;
  discount: number;
  widgetConfiguration: any;
  couponCode: string;
  setCouponCode: React.Dispatch<React.SetStateAction<string>>;
  isApplying: boolean;
  message: { type: 'success' | 'error', text: string } | null;
  applyCoupon: () => void;
  formatDiscount: () => string;
  showCoupon?: boolean;
  showDiscount?: boolean;
  colors?: any;
}

export const PriceSection: React.FC<PriceSectionProps> = ({
  totalPrice,
  discount,
  widgetConfiguration,
  couponCode,
  setCouponCode,
  isApplying,
  message,
  applyCoupon,
  formatDiscount,
  showCoupon = true,
  showDiscount = true,
  colors = {}
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const defaultColors = {
    cardBackground: "#000000F2",
    cardBorder: "#DDB88919",
    cardShadow: "rgba(221,184,137,0.07)",
    cardShadowSelected: "rgba(221,184,137,0.1)",
    titleText: "#DDB889",
    subtitleText: "#DDB889CC",
    infoText: "#DDB88999",
    iconColor: "#DDB889",
    buttonBackground: "#DDB889",
    buttonText: "#000000",
    selectionBackground: "#DDB889",
    selectionIcon: "#000000"
  };

  // Merge default colors with provided colors
  const mergedColors = { ...defaultColors, ...colors };

  // Create style objects from the colors
  const cardStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    boxShadow: `0 0 15px ${mergedColors.cardShadow}`
  };

  const iconStyle = {
    color: mergedColors.iconColor
  };

  const textStyle = {
    color: mergedColors.subtitleText
  };

  const priceStyle = {
    color: mergedColors.titleText
  };

  const inputStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    color: mergedColors.titleText
  };

  const inputHoverStyle = {
    borderColor: `${mergedColors.cardBorder}`,
    boxShadow: `0 0 15px ${mergedColors.cardShadow}`
  };

  const buttonStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    color: mergedColors.titleText
  };

  const buttonHoverStyle = {
    boxShadow: `0 0 15px ${mergedColors.cardShadowSelected}`,
    borderColor: `${mergedColors.cardBorder}`
  };

  const gradientStyle = {
    backgroundImage: `linear-gradient(to right, transparent, ${mergedColors.titleText}10, transparent)`
  };

  return (
    <div
      className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-6 lp-transition-all lp-duration-700 hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]"
      style={cardStyle}
    >
      <SectionTitle
        icon={widgetConfiguration.currency.symbol}
        title={t('price.totalPrice')} /* CHANGED: "Total Price" */
        colors={mergedColors}
      />
      <div className="lp-flex lp-items-center lp-justify-between lp-group">
        <p className="lp-flex lp-items-center">
          {widgetConfiguration?.currency?.symbol ? (
            <span
              className="lp-mr-3 lp-shrink-0 lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
              style={iconStyle}
            >
              {widgetConfiguration.currency.symbol}
            </span>
          ) : (
            <DollarSign
              size={20}
              className="lp-mr-3 lp-shrink-0 lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
              style={iconStyle}
            />
          )}
          <span
            className="lp-transition-colors lp-duration-200 group-hover:lp-text-[#DDB889]"
            style={textStyle}
          >
            {t('price.total')} {/* CHANGED: "Total" */}
          </span>
        </p>
        <p
          className="lp-text-xl lp-font-medium"
          style={priceStyle}
        >
          {widgetConfiguration?.currency?.symbol || '$'}{totalPrice.toFixed(2)}
        </p>
      </div>

      {showDiscount && discount > 0 && (
        <p className="lp-mt-3 lp-flex lp-items-center lp-text-green-500 group">
          <Check
            size={20}
            className="lp-mr-3 lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
          />
          <span className="lp-transition-colors lp-duration-200">
            {t('price.discountApplied')}: {formatDiscount()} {/* CHANGED: "Discount applied:" */}
          </span>
        </p>
      )}

      {showCoupon && widgetConfiguration.isGiftActivated && (
        <div className="lp-mt-6 lp-space-y-3">
          <div className="lp-flex lp-items-center lp-space-x-3">
            <div className="lp-relative lp-flex-grow">
              <Tag
                size={20}
                className="lp-absolute lp-left-3 lp-top-1/2 lp-transform -lp-translate-y-1/2 lp-transition-transform lp-duration-200"
                style={iconStyle}
              />
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder={t('price.enterGiftCode')} /* CHANGED: "Enter gift code" */
                className="lp-w-full lp-pl-10 lp-pr-3 lp-py-3 lp-border lp-rounded-xl lp-transition-all lp-duration-200 focus:lp-outline-none focus:lp-border-[#DDB889]/30 focus:lp-shadow-[0_0_15px_rgba(221,184,137,0.07)] hover:lp-border-[#DDB889]/20 hover:lp-shadow-[0_0_15px_rgba(221,184,137,0.05)] placeholder:lp-text-[#DDB889]/40"
                style={inputStyle}
              />
            </div>
            <button
              onClick={applyCoupon}
              disabled={isApplying}
              className="lp-border lp-py-3 lp-px-6 lp-rounded-xl lp-transition-all lp-duration-200 hover:lp-shadow-[0_0_15px_rgba(221,184,137,0.1)] hover:lp-border-[#DDB889]/20 disabled:lp-opacity-50 disabled:lp-cursor-not-allowed disabled:hover:lp-shadow-none group relative overflow-hidden"
              style={buttonStyle}
            >
              <span className="relative lp-z-10">
                {isApplying ? <ButtonLoadingSpinner /> : t('price.apply')} {/* CHANGED: 'Apply' */}
              </span>
              <div className="absolute lp-inset-0 lp-z-0">
                <div
                  className="absolute lp-inset-0 -lp-translate-x-full lp-bg-gradient-to-r lp-from-transparent lp-via-[#DDB889]/10 lp-to-transparent group-hover:lp-translate-x-full lp-transition-transform lp-duration-700 lp-ease-in-out"
                  style={gradientStyle}
                />
              </div>
            </button>
          </div>

          {message && (
            <div className={`lp-flex lp-items-center lp-group ${
              message.type === 'success' ? 'lp-text-green-500' : 'lp-text-red-500'
            }`}>
              {message.type === 'success' ? (
                <Check size={20} className="lp-mr-3 lp-transition-transform lp-duration-200 group-hover:lp-scale-110" />
              ) : (
                <AlertCircle size={20} className="lp-mr-3 lp-transition-transform lp-duration-200 group-hover:lp-scale-110" />
              )}
              <span className="lp-transition-colors lp-duration-200">
                {message.text}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
